import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gosafeApi } from "../../api";
import {
  onLoadingProcedureRooms,
  onLoading,
  onLoaded,
  onLoadProcedureRooms,
  onFailedProcedureRooms,
  onErrorMessage,
  onSuccessMessage,
  changePageSize,
  changePage,
  onUpdateProcedureRoom,
  onAddProcedureRoom,
} from "../../store/secretary/procedure_rooms";
import { translations, replaceEmptyToNull } from "../../helpers";

export const useProcedureRoomsStore = () => {
  const dispatch = useDispatch();

  const {
    isLoadingData,
    isLoading,
    procedureRooms,
    message,
    pageSize,
    page,
    totalPages,
  } = useSelector((state) => state.procedureRooms);

  const startGetProcedureRooms = useCallback(
    async (page, pageSize) => {
      try {
        dispatch(onLoadingProcedureRooms());

        const { data } = await gosafeApi.get("surgeryRoom", {
          params: { page, pageSize },
        });

        dispatch(onLoadProcedureRooms(data));
      } catch (error) {
        dispatch(
          onFailedProcedureRooms(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startChangePageSize = useCallback(
    async (pageSize) => {
      dispatch(changePageSize(pageSize));
      await startGetProcedureRooms(page, pageSize);
    },
    [dispatch, startGetProcedureRooms, page]
  );

  const startChangePage = useCallback(
    async (page) => {
      dispatch(changePage(page));
      await startGetProcedureRooms(page, pageSize);
    },
    [dispatch, startGetProcedureRooms, pageSize]
  );

  const startAddPage = useCallback(async () => {
    if (totalPages > parseInt(page)) {
      const newPage = (parseInt(page) + 1).toString();
      dispatch(changePage(newPage));
      await startGetProcedureRooms(newPage, pageSize);
    }
  }, [dispatch, startGetProcedureRooms, page, pageSize, totalPages]);

  const startRemovePage = useCallback(async () => {
    if (parseInt(page) > 1) {
      const newPage = (parseInt(page) - 1).toString();
      dispatch(changePage(newPage));
      await startGetProcedureRooms(newPage, pageSize);
    }
  }, [dispatch, startGetProcedureRooms, page, pageSize, totalPages]);

  const reloadProcedureRooms = useCallback(async () => {
    await startGetProcedureRooms(page, pageSize);
  }, [startGetProcedureRooms, page, pageSize]);

  const startUpdateProcedureRoom = useCallback(
    async (procedureRoom) => {
      try {
        dispatch(onLoading());

        // console.log("procedureRoom", procedureRoom);

        const { id, ...newProcedureRoom } = replaceEmptyToNull(procedureRoom);

        newProcedureRoom.number = newProcedureRoom.number
          ? Number(newProcedureRoom.number)
          : null;

        // console.log("id", id);
        // console.log("newProcedureRoom", newProcedureRoom);

        const { data } = await gosafeApi.put(
          `surgeryRoom/${id}`,
          newProcedureRoom
        );

        dispatch(onUpdateProcedureRoom(data.procedureRoom));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const starRegisterProcedureRoom = useCallback(
    async (procedureRoom) => {
      try {
        dispatch(onLoading());

        // console.log("procedureRoom", procedureRoom);

        const newProcedureRoom = replaceEmptyToNull(procedureRoom);
        newProcedureRoom.number = newProcedureRoom.number
          ? Number(newProcedureRoom.number)
          : null;

        // console.log("newProcedureRoom", newProcedureRoom);

        const { data } = await gosafeApi.post("surgeryRoom", newProcedureRoom);

        dispatch(onAddProcedureRoom(data.procedureRoom));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  return {
    // Props
    isLoadingData,
    isLoading,
    procedureRooms,
    message,
    pageSize,
    page,
    totalPages,

    // Methods
    startGetProcedureRooms,
    startChangePageSize,
    startChangePage,
    startAddPage,
    startRemovePage,
    reloadProcedureRooms,
    startUpdateProcedureRoom,
    starRegisterProcedureRoom,
  };
};
